import React from "react";
import {
  CircularProgress,
  Button as MUIButton,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";

export interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "contained" | "outlined" | "text";
  color?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  disabled?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium" | "large";
  style?: React.CSSProperties;
  // Breakpoint-aware styling; `style` is inline and would beat it, so a value
  // set here must not also be set in `style`.
  sx?: SxProps<Theme>;
  roundedBorder?: boolean;
  loading?: boolean;
  endAdornment?: JSX.Element;
  startAdornment?: JSX.Element;
  component?: React.ElementType;
  ref?: React.Ref<any>;
}

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = "outlined",
  color = "primary",
  disabled = false,
  fullWidth = false,
  size = "medium",
  style = {},
  sx,
  roundedBorder = false,
  loading = false,
  endAdornment = null,
  startAdornment = null,
  component = "button",
  ref,
}) => {
  const theme = useTheme();

  return (
    <MUIButton
      disabled={disabled || loading}
      {...(color ? { color } : {})}
      color={color}
      variant={variant}
      onClick={onClick}
      fullWidth={fullWidth}
      size={size}
      ref={ref}
      {...(loading
        ? {
            startIcon: (
              <CircularProgress
                size={15}
                color={variant === "contained" ? "secondary" : "primary"}
                style={{ marginRight: theme.spacing(1) }}
              />
            ),
          }
        : {})}
      {...(startAdornment
        ? {
            startIcon: startAdornment,
          }
        : {})}
      {...(endAdornment
        ? {
            endIcon: endAdornment,
          }
        : {})}
      style={{
        ...(!roundedBorder ? { borderRadius: 0 } : {}),
        fontWeight: theme.typography.fontWeightRegular,
        ...style,
      }}
      sx={sx}
      component={component}
    >
      {children}
    </MUIButton>
  );
};

export default Button;
